import type { ProductionDeliverable, ProductionStaticBody } from "@/types/productionBrief.type";
import { parsePaletteEntry } from "./briefParts";
import { BriefSection, BulletList, FieldLabel, SectionHead } from "./BriefPrimitives";

/** "Legal, before it goes out" and its lines, 15 in --co-text2. */
export function LegalLines({ lines }: { lines: string[] }) {
  return (
    <>
      <FieldLabel>Legal, before it goes out</FieldLabel>
      <div className="mt-1.5 flex flex-col gap-1.5">
        {lines.map((line, index) => (
          <p key={`${line}-${index}`} className="whitespace-pre-wrap text-[15px] leading-[1.5] text-co-text2">
            {line}
          </p>
        ))}
      </div>
    </>
  );
}

function ColumnTitle({ children }: { children: string }) {
  return <p className="text-[13px] font-semibold text-co-text">{children}</p>;
}

function Palette({ entries }: { entries: string[] }) {
  const parsed = entries.map((entry) => entry?.trim()).filter(Boolean).map(parsePaletteEntry);
  const chips = parsed.filter((entry) => entry.hex);
  const words = parsed.filter((entry) => !entry.hex).map((entry) => entry.name);
  return (
    <>
      {chips.length ? (
        <div className="mt-2 flex flex-wrap gap-3">
          {chips.map((entry, index) => (
            <div key={`${entry.hex}-${index}`} className="w-14">
              <span
                className="block size-14 rounded-card shadow-[inset_0_0_0_1px_var(--co-line)]"
                style={{ background: entry.hex! }}
                aria-hidden="true"
              />
              {entry.name ? <p className="mt-1.5 text-[12.5px] leading-snug text-co-text2">{entry.name}</p> : null}
              <p className="text-[12px] leading-snug text-co-muted">{entry.hex!.toUpperCase()}</p>
            </div>
          ))}
        </div>
      ) : null}
      {words.length ? <BulletList items={words} className="mt-2.5" /> : null}
    </>
  );
}

export function ArtDirection({ art }: { art: ProductionStaticBody["artDirection"] | undefined }) {
  if (!art) return null;
  const palette = (art.palette ?? []).filter((entry) => entry?.trim());
  const typography = art.typography?.trim();
  const mood = (art.moodWords ?? []).filter((entry) => entry?.trim());
  const avoid = (art.doNots ?? []).filter((entry) => entry?.trim());
  if (!palette.length && !typography && !mood.length && !avoid.length) return null;
  return (
    <BriefSection accent="purple">
      <SectionHead accent="purple" title="Art direction" sub="For the designer and the image generator" />
      <div className="grid grid-cols-1 gap-7 @[36rem]/briefdoc:grid-cols-2 @[52rem]/briefdoc:grid-cols-4">
        {palette.length ? (
          <div className="min-w-0">
            <ColumnTitle>Palette</ColumnTitle>
            <Palette entries={palette} />
          </div>
        ) : null}
        {typography ? (
          <div className="min-w-0">
            <ColumnTitle>Typography</ColumnTitle>
            <p className="mt-2.5 whitespace-pre-wrap text-[13.5px] leading-[1.4] text-co-text2">{typography}</p>
          </div>
        ) : null}
        {mood.length ? (
          <div className="min-w-0">
            <ColumnTitle>Mood</ColumnTitle>
            <BulletList items={mood} className="mt-2.5" />
          </div>
        ) : null}
        {avoid.length ? (
          <div className="min-w-0">
            <ColumnTitle>Avoid</ColumnTitle>
            <BulletList items={avoid} className="mt-2.5" />
          </div>
        ) : null}
      </div>
    </BriefSection>
  );
}

export interface QuietColumn<Row> {
  key: string;
  label: string;
  /** Fixed width in px; "fill" shares the rest. */
  width: number | "fill";
  value: (row: Row) => string | null | undefined;
}

/**
 * A quiet table: a 12.5 muted head row, Manrope 14 rows with hairlines
 * between them. A column no row fills is dropped; a row with nothing in any
 * column is dropped. Scrolls sideways when the columns do not fit.
 */
export function QuietTable<Row>({
  label,
  columns,
  rows,
  rowKey,
}: {
  label: string;
  columns: QuietColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row, index: number) => string;
}) {
  const cell = (row: Row, col: QuietColumn<Row>) => col.value(row)?.trim() ?? "";
  const kept = rows.filter((row) => columns.some((col) => cell(row, col)));
  const shown = columns.filter((col) => kept.some((row) => cell(row, col)));
  if (!kept.length || !shown.length) return null;
  const template = shown.map((col) => (col.width === "fill" ? "minmax(0,1fr)" : `${col.width}px`)).join(" ");
  const minWidth = shown.reduce((sum, col) => sum + (col.width === "fill" ? 160 : col.width), 0);
  return (
    <div className="overflow-x-auto">
      <div role="table" aria-label={label} style={{ minWidth }}>
        <div role="row" className="grid gap-4 py-2.5 shadow-[inset_0_-1px_0_var(--co-line)]" style={{ gridTemplateColumns: template }}>
          {shown.map((col) => (
            <span key={col.key} role="columnheader" className="text-[12.5px] text-co-muted">
              {col.label}
            </span>
          ))}
        </div>
        {kept.map((row, index) => (
          <div
            key={rowKey(row, index)}
            role="row"
            className="grid gap-4 py-2.5 shadow-[inset_0_-1px_0_var(--co-line)]"
            style={{ gridTemplateColumns: template }}
          >
            {shown.map((col) => (
              <span key={col.key} role="cell" className="min-w-0 whitespace-pre-wrap text-[14px] leading-snug text-co-text">
                {cell(row, col)}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

const DELIVERABLE_COLUMNS: QuietColumn<ProductionDeliverable>[] = [
  { key: "channel", label: "Channel", width: 160, value: (row) => row.channel },
  { key: "asset", label: "Asset", width: "fill", value: (row) => row.asset },
  { key: "ratio", label: "Ratio", width: 100, value: (row) => row.aspectRatio },
  { key: "size", label: "Size", width: 140, value: (row) => row.dimensions },
  { key: "length", label: "Length or pages", width: 120, value: (row) => row.durationOrPages },
  { key: "file", label: "File", width: 80, value: (row) => row.fileFormat },
  { key: "notes", label: "Notes", width: "fill", value: (row) => row.notes },
];

/** Deliverables as a quiet table; a column no row fills is dropped. */
export function Deliverables({
  items,
  legal,
  sub,
}: {
  sub?: string;
  items?: ProductionDeliverable[];
  /** Static briefs close on the legal line; video briefs carry it in Rules. */
  legal?: string[];
}) {
  const rows = (items ?? []).filter((row) => DELIVERABLE_COLUMNS.some((col) => col.value(row)?.trim()));
  const legalLines = (legal ?? []).map((line) => line?.trim()).filter(Boolean);
  if (!rows.length && !legalLines.length) return null;
  return (
    <BriefSection accent="purple">
      <SectionHead accent="purple" title="Deliverables" sub={sub} />
      {rows.length ? (
        <QuietTable
          label="Deliverables"
          columns={DELIVERABLE_COLUMNS}
          rows={rows}
          rowKey={(row, index) => row.id ?? `${row.channel}-${index}`}
        />
      ) : null}
      {legalLines.length ? (
        <div className="mt-6">
          <LegalLines lines={legalLines as string[]} />
        </div>
      ) : null}
    </BriefSection>
  );
}
